import envConfig from "@/config/envConfig";
import { getListQuery } from "@/graphql";
import { objToString } from "@/graphql/helper";
import { NEWS, NEWS_TITLES, NEWS_WITH_BYLINE, QUERY_TOTAL_NEWS_COUNT } from "@/graphql/news";
import { queryRecentNews } from "@/graphql/query";
import {
  ArticleBookmarkType,
  ArticleCommentType,
  ArticleListType,
  ArticleType,
} from "@/types/article";
import { getAuthHeaders } from "@/utils/authToken";
import { notFound } from "next/navigation";

export const fetchNews = async ({
  categoryId,
  subCategoryId,
  isTrending,
  limit = 10,
  sort = "-publishedAt",
  cache = "no-cache",
  withByline = false,
  language,
}: {
  categoryId?: string;
  subCategoryId?: string;
  isTrending?: boolean;
  limit?: number;
  sort?: "publishedAt" | "-publishedAt" | "-trendingPosition" | "trendingPosition" | "-views";
  cache?: RequestCache;
  withByline?: boolean;
  language?: string;
}) => {
  try {
    const response = await fetch(envConfig.publicQraphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: withByline
          ? NEWS_WITH_BYLINE({ categoryId, subCategoryId, isTrending, language })
          : NEWS({ categoryId, subCategoryId, isTrending, language }),
        variables: {
          limit,
          sort,
        },
      }),
      cache,
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data?.allNews?.docs || []) as ArticleListType[];
  } catch (err) {
    console.log(err);
    return [];
  }
};

export const fetchPaginatedNews = async ({
  categoryId,
  subCategoryId,
  isTrending,
  limit = 10,
  sort = "-publishedAt",
  cache = "no-cache",
  page,
  lang,
}: {
  categoryId?: string;
  subCategoryId?: string;
  isTrending?: boolean;
  limit?: number;
  sort?: "publishedAt" | "-publishedAt" | "-trendingPosition" | "-views";
  cache?: RequestCache;
  page: number;
  lang: string;
}) => {
  try {
    const query = new URLSearchParams({
      limit: String(limit),
      sort,
      page: String(page),
      depth: "1",
      locale: lang,
      "where[status][equals]": "published",
      "where[language][equals]": lang,
    });

    if (categoryId) {
      query.set("where[categoryId][equals]", categoryId);
    }

    if (subCategoryId) {
      query.set("where[subCategoryId][equals]", subCategoryId);
    }

    if (typeof isTrending === "boolean") {
      query.set("where[isTrending][equals]", String(isTrending));
    }

    const response = await fetch(`${envConfig.publicServerUrl}/api/news?${query.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache,
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return { data: respData };
  } catch (err) {
    // return [];
  }
};

export const fetchNewsTitles = async ({
  limit = 10,
  sort = "-publishedAt",
  isTrending,
  cache = "no-cache",
  language,
}: {
  limit?: number;
  sort?: "publishedAt" | "-publishedAt" | "-trendingPosition" | "-views";
  isTrending?: boolean;
  cache?: RequestCache;
  language: string;
}) => {
  try {
    const response = await fetch(envConfig.publicQraphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: NEWS_TITLES({ isTrending, language }),
        variables: {
          limit,
          sort,
        },
      }),
      cache,
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data?.allNews?.docs || []) as ArticleListType[];
  } catch (err) {
    console.log(err);
  }
};

export const fetchSingleDetailNews = async ({
  slug,
  cache = "no-cache",
  token,
}: {
  slug: string;
  cache?: RequestCache;
  token?: string;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/slug/${encodeURIComponent(slug)}`,
      {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      cache,
    });

    if (!response.ok) {
      throw Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();

    return (respData?.data || null) as ArticleType;
  } catch (err) {
    return notFound();
  }
};

/** Same as fetchSingleDetailNews but returns null instead of triggering notFound(). */
export const fetchNewsBySlugOptional = async ({
  slug,
  cache = "no-cache",
  token,
}: {
  slug: string;
  cache?: RequestCache;
  token?: string;
}): Promise<ArticleType | null> => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/slug/${encodeURIComponent(slug)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        cache,
      },
    );

    if (!response.ok) return null;

    const respData = await response.json();
    return (respData?.data || null) as ArticleType | null;
  } catch {
    return null;
  }
};

export const fetchNewsByTag = async ({
  tagSlug,
  limit,
  page,
  lang,
}: {
  tagSlug: string;
  limit: number;
  page: number;
  lang?: string;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/tag/${tagSlug}?limit=${limit}&page=${page}&lang=${lang}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return respData;
  } catch (err) {
    console.error("Error:", err);
    return err;
  }
};

export const fetchNewsBySubCategory = async ({
  subCategorySlug,
  categorySlug,
  limit,
  page,
  cache = "no-cache",
  lang,
}: {
  subCategorySlug: string;
  categorySlug?: string;
  limit: number;
  page: number;
  cache?: RequestCache;
  lang?: string;
}) => {
  try {
    const query = new URLSearchParams({
      limit: String(limit),
      page: String(page),
      lang: String(lang || ""),
    });
    if (categorySlug) query.set("category", categorySlug);

    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/sub-category/${subCategorySlug}?${query.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache,
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    return await response.json();
  } catch (err) {
    console.error("fetchNewsBySubCategory:", err);
    return err;
  }
};

export const fetchNewsByCategory = async ({
  categoryId,
  subCategoryId,
  isTrending,
  limit,
  cache = "no-cache",
  sort,
  lang,
  exclude,
}: {
  categoryId: string;
  subCategoryId?: string;
  isTrending?: boolean;
  limit: number;
  cache?: RequestCache;
  sort?: string;
  lang?: string;
  exclude?: string[];
}): Promise<ArticleListType[]> => {
  try {
    const response = await fetch(envConfig.publicQraphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: NEWS({ categoryId, subCategoryId, isTrending, language: lang, exclude }),
        variables: {
          limit,
          sort,
        },
      }),
      cache,
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data?.allNews?.docs || []) as ArticleListType[];
  } catch (err) {
    console.log(err);
    return [];
  }
};

export const fetchConditionalNewsAPI = async ({
  block,
  pageSlug,
  limit,
  lang,
  itemIndex: itemIndexOverride,
}: {
  block: any;
  pageSlug: string;
  limit?: number;
  lang?: string;
  itemIndex?: string;
}) => {
  limit && (block.limit = limit);

  const carouselSettings = block?.carouselSettings;
  const normalizedCarouselSettings =
    carouselSettings && typeof carouselSettings === "object"
      ? {
          ...carouselSettings,
          category:
            typeof carouselSettings?.category === "object" &&
            carouselSettings?.category !== null
              ? (carouselSettings.category as { id?: string; _id?: string; value?: string }).id ||
                (carouselSettings.category as { id?: string; _id?: string; value?: string })._id ||
                (carouselSettings.category as { id?: string; _id?: string; value?: string }).value ||
                carouselSettings.category
              : carouselSettings?.category,
          subCategory:
            typeof carouselSettings?.subCategory === "object" &&
            carouselSettings?.subCategory !== null
              ? (carouselSettings.subCategory as { id?: string; _id?: string; value?: string }).id ||
                (carouselSettings.subCategory as { id?: string; _id?: string; value?: string })._id ||
                (carouselSettings.subCategory as { id?: string; _id?: string; value?: string }).value ||
                carouselSettings.subCategory
              : carouselSettings?.subCategory,
        }
      : carouselSettings;

  // Normalize category and subCategory to IDs if they're objects
  const normalizedBlock = {
    ...block,
    carouselSettings: normalizedCarouselSettings,
    category:
      typeof block?.category === "object" && block?.category !== null
        ? block.category.id || block.category._id || block.category.value || block.category
        : block?.category,
    subCategory:
      typeof block?.subCategory === "object" && block?.subCategory !== null
        ? block.subCategory.id || block.subCategory._id || block.subCategory.value || block.subCategory
        : block?.subCategory,
  };

  try {
    // Generate itemIndex from block id or create a unique identifier from block properties
    const itemIndex =
      itemIndexOverride ||
      block?.id ||
      block?._id ||
      `${block?.blockType || "block"}_${block?.category || ""}_${block?.subCategory || ""}_${block?.orderBy || "latest"}_${pageSlug}`.replace(
        /[^a-zA-Z0-9_]/g,
        "_",
      );
    const blockType = block?.blockType;

    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/blocks/news`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        block: normalizedBlock,
        pageSlug,
        itemIndex,
        blockType,
        lang: lang || "te",
      }),
    });

    const data = await response.json();
    return data?.data;
  } catch (err) {
    console.log("Error in fetchConditionalNewsAPI", err);
    return [];
  }
};

export const fetchPopularNewsAPI = async ({
  language,
  isVideo,
}: {
  language: string;
  isVideo: boolean;
}) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/blocks/popular`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ language, isVideo }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const data = await response.json();

    return data?.data;
  } catch (err) {
    console.error("Error in fetchPopularNewsAPI: ", err);
    return err;
  }
};

export const fetchTrendingNewsAPI = async ({ language }: { language: string }) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/blocks/trending`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        language,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const data = await response.json();

    return data?.data;
  } catch (err) {
    console.error("Error:", err);
    return err;
  }
};

export const PostRecommendedNews = async ({ newsId }: { newsId: string }) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/news/recommended`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ newsId }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    return await response.json();
  } catch (err) {
    console.error("Error:", err);
    return err;
  }
};

export const fetchRecommendedNews = async ({
  limit,
  cache = "no-cache",
  language,
}: {
  limit: number;
  cache?: RequestCache;
  language?: string;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/recommended?limit=${limit}&lang=${language}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache,
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data || []) as ArticleType[];
  } catch (err) {
    console.error("Error:", err);
    return [] as ArticleType[];
  }
};

export const fetchRelatedNews = async ({
  slug,
  limit,
  cache = "no-cache",
}: {
  slug: string;
  limit: number;
  cache?: RequestCache;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/related?slug=${slug}&limit=${limit}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache,
      },
    );

    if (!response.ok) {
      throw Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data || []) as ArticleType[];
  } catch (err) {
    console.error("Error:", err);
    return [] as ArticleType[];
  }
};

export const postNewsLike = async ({ newsId, isLiked }: { newsId: string; isLiked: boolean }) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/news/like`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        newsId,
        isLiked,
      }),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData;
  } catch (err) {
    throw err;
  }
};

export const postNewsBookmark = async ({
  newsId,
  isBookmarked,
}: {
  newsId: string;
  isBookmarked: boolean;
}) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/news/bookmark`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        newsId,
        isBookmarked,
      }),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData;
  } catch (err) {
    throw err;
  }
};

export const fetchUserBookmarks = async () => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/news/user-bookmark`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData as { bookmarks: ArticleBookmarkType[] };
  } catch (err) {
    throw err;
  }
};

export const fetchNewsActivityStatus = async ({ newsId }: { newsId: string }) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/activity-status/${newsId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData as { isLiked: boolean; likesCount: number; isBookmarked: boolean };
  } catch (err) {
    throw err;
  }
};

export const postNewsComment = async ({
  newsId,
  comment,
  parentComment,
}: {
  newsId: string;
  comment: string;
  parentComment?: string;
}) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/users/news-comments`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        newsId,
        comment,
        ...(parentComment && { parentComment }),
      }),
      credentials: "include",
    });

    if (!response.ok) {
      let message = "Something went wrong!";
      try {
        const errorData = await response.json();
        message = errorData?.message || errorData?.error || message;
      } catch {
        // ignore
      }
      const error = new Error(message) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    const respData = await response.json();
    return respData as { comment: ArticleCommentType };
  } catch (err) {
    throw err;
  }
};

export const fetchNewsComments = async ({
  newsId,
  limit = 10,
  page = 1,
  sortBy = "latest",
}: {
  newsId: string;
  limit?: number;
  page?: number;
  sortBy?: string;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/users/news-comments/${newsId}?limit=${limit}&page=${page}&sortBy=${sortBy}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData as { totalDocs: number; comments: ArticleCommentType[] };
  } catch (err) {
    throw err;
  }
};

export const fetchNewsByIds = async ({ newsIds }: { newsIds: string[] }) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/news/by-ids`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ newsIds }),
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData?.newsList as ArticleListType[];
  } catch (err) {
    throw err;
  }
};

export const fetchTotalNewsCount = async () => {
  try {
    const response = await fetch(envConfig.publicQraphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: QUERY_TOTAL_NEWS_COUNT,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data?.allNews?.totalDocs || 0) as number;
  } catch (err) {
    console.log(err);
  }
};

export const fetchAllNewsSlug = async ({
  page = 1,
  limit = 10,
}: {
  page?: number;
  limit?: number;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/slugs-list?page=${page}&limit=${limit}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData || []) as ArticleListType[];
  } catch (err) {
    console.log(err);
  }
};

export const fetchNewsById = async (newsID: string) => {
  try {
    if (!newsID || newsID.trim() === "") {
      throw new Error("News ID is required");
    }

    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/news/newsid/${newsID}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-cache",
    });

    if (!response.ok) {
      // Try to get error message from response
      let errorMessage = "Something went wrong!";
      try {
        const errorData = await response.json();
        errorMessage = errorData?.error || errorData?.message || `Error: ${response.statusText}`;
      } catch {
        errorMessage = `Error: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const respData = await response.json();

    if (!respData?.news) {
      throw new Error("News not found");
    }

    return respData.news as ArticleListType;
  } catch (err: any) {
    console.error("fetchNewsById error:", err);
    throw err;
  }
};

// lates news list page -> fetch all latest news with pagination.
export const fetchLatestNews = async ({
  limit,
  page,
  lang,
  /** Extra rows to skip before pagination (e.g. 3 = start at 4th newest, same sort as Latest) */
  skip = 0,
}: {
  limit: number;
  page: number;
  lang?: string;
  skip?: number;
}) => {
  try {
    const skipParam = skip > 0 ? `&skip=${skip}` : "";
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/latest?limit=${limit}&page=${page}&lang=${lang}${skipParam}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    return await response.json();
  } catch (err) {
    console.error("Error in fetchingLatestNews:", err);
    return err;
  }
};
